import { InputHTMLAttributes, FocusEventHandler, KeyboardEventHandler, forwardRef } from 'react'
import { cn } from '../../utils'
import styles from './Input.module.css'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Stretch to fill the available width */
  block?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  function Input({ block, className, ...rest }, ref) {
    return <input ref={ref} className={cn(styles.input, block && styles.block, className)} {...rest} />
  },
)

interface SearchFieldProps {
  value: string
  onChange: (value: string) => void
  onSubmit?: (value: string) => void
  onClear?: () => void
  onFocus?: FocusEventHandler<HTMLInputElement>
  onBlur?: FocusEventHandler<HTMLInputElement>
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
  placeholder?: string
  /** Bootstrap icon class for the leading glyph (default: search) */
  icon?: string
  block?: boolean
  className?: string
  autoFocus?: boolean
}

/** Icon + input + clear button — the unified search/entry field. */
export function SearchField({
  value,
  onChange,
  onSubmit,
  onClear,
  onFocus,
  onBlur,
  onKeyDown,
  placeholder = 'Search…',
  icon = 'bi bi-search',
  block,
  className,
  autoFocus,
}: SearchFieldProps) {
  return (
    <div className={cn(styles.field, block && styles.block, className)}>
      <i className={icon} aria-hidden="true" />
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        autoFocus={autoFocus}
        onChange={e => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={e => {
          if (e.key === 'Enter' && onSubmit) onSubmit(value)
          onKeyDown?.(e)
        }}
      />
      {value && (
        <button
          type="button"
          className={styles.clear}
          aria-label="Clear"
          onClick={() => {
            onChange('')
            onClear?.()
            onSubmit?.('')
          }}
        >
          <i className="bi bi-x-lg" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}
